/**
 * Discovery Sources Tab
 * Phase 1: Manage training_sources (discovery feeds)
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useDiscoverySources,
  useUpdateSourceStatus,
  useDisableSource,
  useRunDiscoveryNow,
  type TrainingSource,
} from '@/hooks/useDiscoverySources';
import { DiscoverySummaryCard } from './DiscoverySummaryCard';
import { AddEditSourceDialog } from './AddEditSourceDialog';
import {
  Rss,
  Youtube,
  BookOpen,
  Calendar,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Plus,
  MoreHorizontal,
  Play,
  Edit,
  Trash,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const SOURCE_TYPE_ICONS: Record<string, React.ReactNode> = {
  youtube_channel: <Youtube className="h-4 w-4 text-red-500" />,
  youtube_playlist: <Youtube className="h-4 w-4 text-red-500" />,
  google_books: <BookOpen className="h-4 w-4 text-blue-500" />,
  calendar: <Calendar className="h-4 w-4 text-green-500" />,
  rss: <Rss className="h-4 w-4 text-orange-500" />,
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  youtube_channel: 'YouTube Channel',
  youtube_playlist: 'YouTube Playlist',
  google_books: 'Google Books',
  calendar: 'Calendar',
  rss: 'RSS Feed',
};

const ADD_SOURCE_DIALOG_KEY = 'discovery-sources-add-dialog-open';

export function DiscoverySourcesTab() {
  const { data: sources, isLoading, refetch } = useDiscoverySources();
  const updateStatus = useUpdateSourceStatus();
  const disableSource = useDisableSource();
  const runDiscoveryNow = useRunDiscoveryNow();
  
  // Persist add dialog open state
  const [showAddDialog, setShowAddDialog] = useState(() => {
    return sessionStorage.getItem(ADD_SOURCE_DIALOG_KEY) === 'true';
  });
  const [editSource, setEditSource] = useState<TrainingSource | null>(null);

  useEffect(() => {
    sessionStorage.setItem(ADD_SOURCE_DIALOG_KEY, String(showAddDialog));
  }, [showAddDialog]);

  const handleToggle = async (source: TrainingSource, checked: boolean) => {
    try {
      await updateStatus.mutateAsync({ sourceId: source.id, isActive: checked });
      toast.success(`Source ${checked ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update source');
    }
  };

  const handleRunNow = async (sourceId: string) => {
    try {
      const result = await runDiscoveryNow.mutateAsync({ sourceId });
      toast.success(`Discovery complete: ${result.itemsAdded || 0} added, ${result.itemsSkipped || 0} skipped`);
    } catch (error) {
      toast.error('Discovery failed');
    }
  };

  const handleDisable = async (sourceId: string) => {
    try {
      await disableSource.mutateAsync({ sourceId });
      toast.success('Source disabled');
    } catch (error) {
      toast.error('Failed to disable source');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <DiscoverySummaryCard />

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Rss className="h-5 w-5" />
                Discovery Sources
              </CardTitle>
              <CardDescription>
                Manage content discovery feeds from training providers
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button size="sm" onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Source
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!sources || sources.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Rss className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No discovery sources configured</p>
              <p className="text-sm mt-2">
                Click "Add Source" to configure your first discovery feed
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Last Fetch</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Enabled</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sources.map((source) => (
                    <TableRow key={source.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {SOURCE_TYPE_ICONS[source.source_type] || <Rss className="h-4 w-4" />}
                          <span className="font-medium truncate max-w-[180px]">
                            {source.source_name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {SOURCE_TYPE_LABELS[source.source_type] || source.source_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {source.provider?.display_name || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        {source.last_fetched_at ? (
                          <div className="text-sm">
                            <div className="text-muted-foreground">
                              {formatDistanceToNow(new Date(source.last_fetched_at), { addSuffix: true })}
                            </div>
                            {source.fetch_count !== null && (
                              <div className="text-xs text-muted-foreground/70">
                                {source.fetch_count} items
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">Never</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {source.last_fetch_status === 'success' ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : source.last_fetch_status === 'error' ? (
                          <XCircle className="h-4 w-4 text-destructive" />
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Switch
                          checked={source.is_active}
                          onCheckedChange={(checked) => handleToggle(source, checked)}
                          disabled={updateStatus.isPending}
                        />
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => handleRunNow(source.id)}
                              disabled={runDiscoveryNow.isPending}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Run Now
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditSource(source)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDisable(source.id)}
                              className="text-destructive"
                            >
                              <Trash className="h-4 w-4 mr-2" />
                              Disable
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Add Source Dialog */}
      <AddEditSourceDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
      />

      {/* Edit Source Dialog */}
      <AddEditSourceDialog
        open={!!editSource}
        onOpenChange={(open) => !open && setEditSource(null)}
        source={editSource}
      />
    </div>
  );
}

export default DiscoverySourcesTab;
